import { useQuery } from '@apollo/client/react';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Chip,
  CircularProgress,
  Autocomplete as MUIAutocomplete,
  Paper,
  TextField,
} from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  SelfDocument,
  UserAutocompleteDetailsFragment,
} from '../../../graphql/types';
import useTracking, { TrackingEventData } from '../../../hooks/useTracking';
import {
  BLACK,
  BLACK2,
  BORDER_GRAY,
  COOL_GRAY100,
  HOVER,
  ICON_SECONDARY,
  TEXT_DISABLED,
  WHITE,
} from '../../../theme';
import { getMemberName } from '../../../utils/getMemberName';
import { EventLabels, EventNames } from '../../../utils/trackingEvents';
import Avatar from '../Avatar';
import Errors from '../ErrorMessage';
import Text, { TextColor, TextKind, textStyleFrom } from '../Text';

const StyledAutoCompleteContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 8px;
`;

const StyledMuiAutocomplete: typeof MUIAutocomplete = styled(MUIAutocomplete)`
  .MuiAutocomplete-inputRoot {
    padding: 0 40px 0 0 !important;
  }
  fieldset {
    border: none !important;
  }
`;

const TextInputFontStyles = textStyleFrom(TextKind.BodyM, TextColor.Primary);

const StyledMuiTextField = styled(TextField)`
  border-radius: 8px !important;
  border: 1px solid ${BORDER_GRAY} !important;
  background: ${WHITE};
  box-sizing: border-box;

  input {
    padding: 8px !important;

    & {
      ${{ ...TextInputFontStyles }}
    }
    &::placeholder,
    &::-webkit-input-placeholder {
      color: ${ICON_SECONDARY};
      opacity: 1;
    }
  }

  .Mui-disabled {
    cursor: default;

    color: ${TEXT_DISABLED} !important;
    background: ${COOL_GRAY100};
    -webkit-text-fill-color: ${TEXT_DISABLED} !important; // really?
  }
`;

// Selection is marked with a checkmark rather than a disabled treatment, so
// selected members can be deselected from the list.
const StyledOption = styled.li`
  padding: 8px 16px;
  display: flex;
  gap: 8px;
  align-items: center;
  background: white;
  cursor: pointer;

  /* The custom listbox slot replaces MUI's AutocompleteListbox, which is where
     the option hover/keyboard highlight is normally defined, so restore it. */
  &.Mui-focused {
    background: ${HOVER};
  }
`;

const SelectedCheckIcon = styled(FontAwesomeIcon)`
  margin-left: auto;
  color: ${BLACK2};
`;

const StyledChip = styled(Chip)`
  color: ${WHITE};
  background-color: ${BLACK};

  & > svg {
    fill: ${TEXT_DISABLED};
  }
`;

const StyledChipBox = styled(Chip)`
  color: ${BLACK};
  background-color: ${COOL_GRAY100};
  border-radius: 8px;

  & > svg {
    fill: ${BLACK};
  }
`;

const PillContainer = styled.div<{ $showAllPills: boolean }>`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-height: ${({ $showAllPills }) => ($showAllPills ? '200px' : 'auto')};
  overflow-y: ${({ $showAllPills }) => ($showAllPills ? 'auto' : 'visible')};
  padding: ${({ $showAllPills }) => ($showAllPills ? '4px 4px 0' : '0')};
  box-sizing: border-box;
`;

const PillHeader = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const TextActionButton = styled.button`
  ${textStyleFrom(TextKind.LabelM, TextColor.Secondary)}
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${ICON_SECONDARY};

  &:hover {
    color: ${BLACK2};
  }
`;

export type MemberScope = 'assigned' | 'all';

const SCOPE_OPTIONS: { value: MemberScope; label: string }[] = [
  { value: 'assigned', label: 'Assigned members' },
  { value: 'all', label: 'All members' },
];

const SELECT_ALL_TAG_LABELS: Record<MemberScope, string> = {
  assigned: 'All assigned members',
  all: 'All members',
};

const SelectAllRow = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 0 12px 8px;
`;

const SwitcherTrack = styled.div`
  display: flex;
  gap: 4px;
  padding: 4px;
  margin: 8px;
  border-radius: 999px;
  background: ${COOL_GRAY100};
`;

const SwitcherButton = styled.button<{ $active: boolean }>`
  ${textStyleFrom(TextKind.LabelM, TextColor.Primary)}
  flex: 1;
  border: none;
  border-radius: 999px;
  padding: 8px 12px;
  cursor: pointer;
  text-transform: uppercase;
  background: ${({ $active }) => ($active ? WHITE : 'transparent')};
  color: ${({ $active }) => ($active ? BLACK2 : ICON_SECONDARY)};
  box-shadow: ${({ $active }) =>
    $active ? '0px 1px 2px rgba(0, 0, 0, 0.12)' : 'none'};
`;

interface MemberScopeSwitcherProps {
  value: MemberScope;
  onChange: (scope: MemberScope) => void;
}

const MemberScopeSwitcher: React.FC<MemberScopeSwitcherProps> = ({
  value,
  onChange,
}) => (
  <SwitcherTrack
    data-testid="member-scope-switcher"
    // Keep the autocomplete dropdown open when interacting with the switcher.
    onMouseDown={(e) => e.preventDefault()}
  >
    {SCOPE_OPTIONS.map((option) => (
      <SwitcherButton
        key={option.value}
        type="button"
        $active={value === option.value}
        aria-pressed={value === option.value}
        data-testid={`member-scope-${option.value}`}
        onClick={() => onChange(option.value)}
      >
        {option.label}
      </SwitcherButton>
    ))}
  </SwitcherTrack>
);

// Fraction of the visible list height, measured up from the bottom, at which
// the next page is requested.
const LOAD_MORE_SCROLL_THRESHOLD_RATIO = 0.3;

// Scrolls a wrapper around the MUI `<ul role="listbox">` instead of the
// listbox itself, so MUI's scroll resets on option changes land on a
// non-scrolling element and appending a page never jumps back to the top.
const ListboxScrollContainer = styled.div`
  overflow-y: auto;
  /* Appending a page must not make the browser re-anchor the scroll position:
     that emits a scroll event, which would re-fire onLoadMore in a loop. */
  overflow-anchor: none;
`;

interface InfiniteScrollListboxProps
  extends React.HTMLAttributes<HTMLUListElement> {
  /** Called when the user scrolls near the bottom of the options list. */
  onLoadMore?: () => void;
  /** True while the next page is being fetched, to avoid duplicate requests. */
  loadingMore?: boolean;
  /** Changes when the search or scope changes so the list resets to the top. */
  resetKey?: string;
  /** Max height of the scroll area (any CSS length). */
  maxHeight?: string;
  /** Injected by MUI; not a valid DOM attribute, so it must not reach the <ul>. */
  ownerState?: unknown;
}

/**
 * Autocomplete listbox slot that scrolls a wrapper element and requests more
 * options as the user nears the bottom.
 */
const InfiniteScrollListbox = React.forwardRef<
  HTMLUListElement,
  InfiniteScrollListboxProps
>(function InfiniteScrollListbox(
  {
    children,
    onLoadMore,
    loadingMore,
    resetKey,
    maxHeight,
    ownerState: _ownerState,
    style,
    ...listboxProps
  },
  ref,
) {
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  // A new search shows a fresh result set, so start it from the top. resetKey
  // is a trigger-only dependency.
  // biome-ignore lint/correctness/useExhaustiveDependencies: resetKey triggers the reset on a new search
  React.useLayoutEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [resetKey]);

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const container = event.currentTarget;
    const threshold = container.clientHeight * LOAD_MORE_SCROLL_THRESHOLD_RATIO;
    if (
      onLoadMore &&
      !loadingMore &&
      container.scrollTop + container.clientHeight >=
        container.scrollHeight - threshold
    ) {
      onLoadMore();
    }
  };

  return (
    <ListboxScrollContainer
      ref={containerRef}
      onScroll={handleScroll}
      data-testid="autocomplete-listbox-scroll"
      style={{ maxHeight }}
    >
      <ul
        {...listboxProps}
        ref={ref}
        style={{
          ...style,
          listStyle: 'none',
          margin: 0,
          padding: '8px 0',
          maxHeight: 'none',
          overflow: 'visible',
        }}
      >
        {children}
      </ul>
    </ListboxScrollContainer>
  );
});

interface MemberBulkAutoCompleteProps {
  members: UserAutocompleteDetailsFragment[];
  onInputChange?: (value: string) => void;
  onSelection: (members: UserAutocompleteDetailsFragment[]) => void;
  onDeletePill: (deletedUserId: string) => void;
  selectedMemberIds: string[];
  loading?: boolean;
  /** Called when the user scrolls near the bottom of the options list. */
  onLoadMore?: () => void;
  /** True while the next page of members is being fetched. */
  loadingMore?: boolean;
  name?: string;
  trackingData?: TrackingEventData;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  rerenderKey?: number;
  showAllPills?: boolean;
  setShowAllPills?: (show: boolean) => void;
  memberScope?: MemberScope;
  onMemberScopeChange?: (scope: MemberScope) => void;
  onRemoveAll?: () => void;
  /** Fully selected scope; a single "All …" tag replaces the pills. */
  selectAllScope?: MemberScope | null;
  /** Member count for the fully selected scope, shown in the "All …" tag. */
  selectAllCount?: number | null;
  onSelectAllChange?: (scope: MemberScope | null) => void;
  selectorMaxHeight?: string;
}

export const MemberBulkAutocomplete: React.FC<MemberBulkAutoCompleteProps> = ({
  members,
  onInputChange,
  loading,
  onLoadMore,
  loadingMore,
  onSelection,
  onDeletePill,
  selectedMemberIds,
  name,
  trackingData,
  label,
  placeholder,
  disabled,
  rerenderKey = 0,
  showAllPills = false,
  setShowAllPills = () => {},
  memberScope,
  onMemberScopeChange,
  onRemoveAll,
  selectAllScope = null,
  selectAllCount = null,
  onSelectAllChange,
  selectorMaxHeight = '350px',
}) => {
  const { track } = useTracking();
  const { data: selfData } = useQuery(SelfDocument);
  const selfId = selfData?.self?.id;

  const [inputValue, setInputValue] = useState('');

  const handleFocus = () => {
    if (trackingData) {
      track(EventNames.DropDownMenuFocus, {
        event_label: EventLabels.Member,
        ...trackingData,
      });
    }
  };

  const handleOpen = () => {
    if (trackingData) {
      track(EventNames.DropDownMenuClick, {
        event_label: EventLabels.Member,
        ...trackingData,
      });
    }
  };

  const handleSelection = (newValue: UserAutocompleteDetailsFragment[]) => {
    if (trackingData) {
      track(EventNames.DropDownMenuItemClick, {
        event_label: EventLabels.Member,
        ...trackingData,
      });
    }
    onSelection(newValue);
    // Keep the selector open after a pick and reset the search so the full
    // (scoped) member list is shown again for the next selection.
    setInputValue('');
    if (onInputChange) {
      onInputChange('');
    }
  };

  const handleInputChange = (
    _: React.ChangeEvent<{}>,
    newInputValue: string,
    reason: string,
  ) => {
    // MUI fires 'reset' input changes on internal re-syncs with an empty
    // value; reacting to them would wipe the user's in-progress search.
    if (reason === 'reset') {
      return;
    }
    setInputValue(newInputValue);
    if (onInputChange) {
      onInputChange(newInputValue);
    }
  };

  useEffect(() => {
    if (selectedMemberIds.length === 0) {
      setShowAllPills(false);
    }
  }, [selectedMemberIds, setShowAllPills]);

  const selectedMembers = useMemo(
    () => members.filter(({ id }) => selectedMemberIds.includes(id)),
    [members, selectedMemberIds],
  );

  // A "Select all" only marks the displayed list as recipients when it is the
  // fully selected scope (or no scope switcher is in use).
  const selectAllActiveForScope =
    selectAllScope != null &&
    (memberScope == null || selectAllScope === memberScope);

  // Read the scope props from refs so PaperComponent keeps a stable identity;
  // a new identity would remount the Paper and reset the listbox scroll
  // whenever a page is appended.
  const memberScopeRef = useRef(memberScope);
  memberScopeRef.current = memberScope;
  const onMemberScopeChangeRef = useRef(onMemberScopeChange);
  onMemberScopeChangeRef.current = onMemberScopeChange;
  const selectAllScopeRef = useRef(selectAllScope);
  selectAllScopeRef.current = selectAllScope;
  const onSelectAllChangeRef = useRef(onSelectAllChange);
  onSelectAllChangeRef.current = onSelectAllChange;

  // The scope switcher and "Select all" render in a header outside the
  // scrollable listbox, so they stay pinned while the options scroll.
  const showScopeSwitcher = Boolean(memberScope && onMemberScopeChange);
  const showSelectAll = Boolean(onSelectAllChange);
  const PaperComponent = useMemo(() => {
    if (!showScopeSwitcher && !showSelectAll) {
      return undefined;
    }
    const ScopePaper = React.forwardRef<
      HTMLDivElement,
      React.HTMLAttributes<HTMLElement>
    >(({ children, ...paperProps }, ref) => {
      const currentScope = memberScopeRef.current ?? 'assigned';
      const activeSelectAllScope = selectAllScopeRef.current;
      const allSelectedForScope = activeSelectAllScope === currentScope;
      return (
        <Paper {...paperProps} ref={ref}>
          {showScopeSwitcher && (
            <MemberScopeSwitcher
              value={currentScope}
              onChange={(scope) => {
                // Reset the search so the visible input matches the unfiltered
                // list the parent refetches for the new scope.
                setInputValue('');
                onMemberScopeChangeRef.current?.(scope);
              }}
            />
          )}
          {showSelectAll && (
            <SelectAllRow
              // Keep the dropdown open, as with the switcher above.
              onMouseDown={(e) => e.preventDefault()}
            >
              <TextActionButton
                type="button"
                data-testid="select-all-members"
                onClick={() =>
                  onSelectAllChangeRef.current?.(
                    allSelectedForScope ? null : currentScope,
                  )
                }
              >
                {allSelectedForScope ? 'Deselect all' : 'Select all'}
              </TextActionButton>
            </SelectAllRow>
          )}
          {children}
        </Paper>
      );
    });
    ScopePaper.displayName = 'MemberScopePaper';
    return ScopePaper;
  }, [showScopeSwitcher, showSelectAll]);

  return (
    <StyledAutoCompleteContainer>
      {label && (
        <Text
          component="label"
          kind={TextKind.LabelL}
          htmlFor={`memberBulkAutoComplete-${name}`}
        >
          {label}
        </Text>
      )}

      <StyledMuiAutocomplete
        multiple
        disableCloseOnSelect
        data-testid="autocomplete-search"
        id={`memberBulkAutoComplete-${name}`}
        // Before the first fetch, fall back to the selected members so the
        // controlled `value` always resolves to an option; while paginating,
        // keep showing members so the listbox isn't torn down mid-scroll.
        options={
          loading && members.length === 0 ? [...selectedMembers] : [...members]
        }
        value={selectedMembers}
        getOptionLabel={(option) =>
          `${getMemberName(option)}${option.id === selfId ? ' (Me)' : ''}`
        }
        getOptionKey={(option) => option.id}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        onChange={(_, newValue) => handleSelection(newValue)}
        onInputChange={handleInputChange}
        inputValue={inputValue}
        onOpen={() => handleOpen()}
        {...(PaperComponent ? { PaperComponent } : {})}
        slots={{ listbox: InfiniteScrollListbox }}
        slotProps={{
          clearIndicator: { sx: { display: 'none' } },
          popper: {
            placement: 'bottom-start',
            modifiers: [{ name: 'flip', enabled: false }],
          },
          listbox: {
            onLoadMore,
            loadingMore,
            // Reset the scroll to the top on a new search or a scope change,
            // since both replace the list with a fresh result set.
            resetKey: `${memberScope ?? ''}:${inputValue}`,
            maxHeight: selectorMaxHeight,
          } as InfiniteScrollListboxProps,
        }}
        loading={loading}
        onFocus={handleFocus}
        disabled={disabled}
        filterOptions={(x) => x} // Disable built-in filtering
        renderTags={() => null} // Disable tags in the input
        renderInput={(params) => (
          <StyledMuiTextField
            {...params}
            disabled={disabled}
            placeholder={
              selectedMemberIds.length === 0
                ? (placeholder ?? `Select members`)
                : ''
            } // Remove placeholder if there are selected users
            InputProps={{
              ...params.InputProps,
              disabled,
              endAdornment: (
                <>
                  {loading || loadingMore ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => {
          const isSelected =
            selectAllActiveForScope || selectedMemberIds.includes(option.id);
          const { key, ...otherProps } = props;
          return (
            <StyledOption key={key} {...otherProps} aria-selected={isSelected}>
              <Avatar user={option} size={24} />
              <Text kind={TextKind.BodyM}>
                {getMemberName(option)}
                {option.id === selfId ? ' (Me)' : ''}
              </Text>
              {isSelected && (
                <SelectedCheckIcon
                  icon={faCheck}
                  data-testid={`member-option-check-${option.id}`}
                />
              )}
            </StyledOption>
          );
        }}
      />

      {name && <Errors name={name} errorTextKind={TextKind.BodyS} />}

      {onRemoveAll && selectedMemberIds.length > 0 && (
        <PillHeader>
          <TextActionButton
            type="button"
            data-testid="remove-all-members"
            onClick={onRemoveAll}
          >
            Remove all
          </TextActionButton>
        </PillHeader>
      )}

      <PillContainer
        data-testid="autocomplete-select-results"
        $showAllPills={showAllPills}
        key={`pill-container-${rerenderKey}`}
      >
        {selectAllScope ? (
          <StyledChip
            data-testid="select-all-tag"
            label={`${SELECT_ALL_TAG_LABELS[selectAllScope]}${
              selectAllCount != null ? ` (${selectAllCount})` : ''
            }`}
            onDelete={() => onSelectAllChange?.(null)}
          />
        ) : showAllPills || selectedMemberIds.length <= 1 ? (
          members
            .filter(({ id }) => selectedMemberIds.includes(id))
            .map((user) => (
              <StyledChip
                data-testid={`autocomplete-chip-${user.id}`}
                key={user.id}
                label={getMemberName(user)}
                onDelete={() => onDeletePill(user.id)}
              />
            ))
        ) : (
          <>
            <StyledChip
              label={getMemberName(
                members.find(({ id }) => id === selectedMemberIds[0]),
              )}
              onDelete={() => onDeletePill(selectedMemberIds[0])}
            />
            <StyledChipBox
              label={`+${selectedMemberIds.length - 1} more`}
              onClick={() => setShowAllPills(true)}
            />
          </>
        )}
      </PillContainer>
    </StyledAutoCompleteContainer>
  );
};
